import { api } from '../api';
import { links } from '../../../data/links';
import { extractPageFromUrl } from '../../../utils/utils';

export const searchApi = api.injectEndpoints({
  endpoints: build => ({
    getSearchRealties: build.infiniteQuery({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: lastPage => extractPageFromUrl(lastPage?.links?.next),
      },
      query: ({
        queryArg: {
          token,
          search_type,
          search_query,
          search_query_request,
          search_filters,
          sort_by,
          like_ids,
        },
        pageParam: page,
      }) => {
        const searchType = search_type
          ? `search_type=${search_type}`
          : 'search_type=1';

        let newUrl = `${links.search}?${searchType}`;

        if (like_ids && like_ids.length > 0) {
          newUrl += like_ids.map(id => `&id[]=${id}`).join('');
        } else {
          if (search_query) {
            newUrl += `&search_query=${search_query}`;
          }

          if (search_filters && search_filters.length > 0) {
            const cleanFilters = search_filters.replace(/^[?&]/, '');
            newUrl += `&${cleanFilters}`;
          }

          if (sort_by) {
            newUrl += `&sort_by=${sort_by}`;
          }

          if (search_query_request) {
            newUrl += `&search_query_request=${search_query_request}`;
          }
        }

        if (page) {
          newUrl += `&page=${page}`;
        }

        newUrl += '&sold_realty=strict';

        const params = {
          url: newUrl,
          headers: { Authorization: `Bearer ${token}` },
        };
        return params;
      },
      transformResponse: responseData => {
        const searchData = responseData?.data;
        if (searchData) {
          return searchData;
        } else {
          return {};
        }
      },
      providesTags: result => [
        ...(result?.pages ?? []).flatMap(({ realties = [] }) =>
          realties.map(({ id }) => ({ type: 'SearchRealties', id }))
        ),
        { type: 'SearchRealties', id: 'LIST' },
      ],
    }),
    getSearchRealtiesMap: build.query({
      query: ({ token, search_query, search_filters }) => {
        let newUrl = `${links.searchMap}?search_type=1`;

        if (search_query) {
          newUrl += `&search_query=${search_query}`;
        }

        if (search_filters && search_filters.length > 0) {
          const cleanFilters = search_filters
            .replace(/^[?&]/, '')
            .replace(/^search_type=\d+&?/, '');
          if (cleanFilters) newUrl += `&${cleanFilters}`;
        }

        // Always exclude ALL sold realties from map results (no 60-day grace period)
        newUrl += '&sold_realty=strict';

        const params = {
          url: newUrl,
          headers: { Authorization: `Bearer ${token}` },
        };

        return params;
      },
      transformResponse: (responseData, meta, arg) => {
        const realtiesData = responseData?.data?.markers;

        if (realtiesData) {
          return realtiesData;
        } else {
          return [];
        }
      },
      providesTags: result =>
        Array.isArray(result)
          ? [
              ...result.map(({ id }) => ({
                type: 'SearchRealtiesMap',
                id,
              })),
              { type: 'SearchRealtiesMap', id: 'LIST' },
            ]
          : [{ type: 'SearchRealtiesMap', id: 'LIST' }],
    }),
    getSearchInputRealties: build.query({
      query: ({ token, search_query }) => {
        let newUrl = `${links.search}${search_query ? `?search_query=${search_query}&search_type=1` : '?search_type=1'}`;

        const params = {
          url: newUrl,
          headers: { Authorization: `Bearer ${token}` },
        };

        return params;
      },
      transformResponse: responseData => {
        const searchData = responseData?.data;

        if (searchData) {
          return searchData;
        } else {
          return {};
        }
      },
      providesTags: result =>
        result?.realties
          ? [
              ...result.realties.map(({ id }) => ({
                type: 'SearchRealtiesInput',
                id,
              })),
              { type: 'SearchRealtiesInput', id: 'LIST' },
            ]
          : [{ type: 'SearchRealtiesInput', id: 'LIST' }],
    }),
    getSearchFiltersData: build.query({
      query: token => ({
        url: links.filters,
        headers: { Authorization: `Bearer ${token}` },
      }),
      transformResponse: responseData => {
        const filtersData = responseData?.data;

        if (filtersData) {
          return filtersData;
        } else {
          return {};
        }
      },
    }),
    getRealtiesByIds: build.query({
      queryFn: async ({ token, ids }, _api, _extraOptions, baseQuery) => {
        if (!ids || ids.length === 0) {
          return { data: { realties: [], links: {} } };
        }
        const url = `${links.search}?search_type=1${ids.map(id => `&id[]=${id}`).join('')}&sold_realty=strict`;
        const result = await baseQuery({ url, headers: { Authorization: `Bearer ${token}` } });
        if (result.error) return { error: result.error };
        return { data: result.data?.data ?? {} };
      },
      providesTags: result => [
        ...(result?.realties ?? []).map(({ id }) => ({ type: 'SearchRealties', id })),
        { type: 'SearchRealties', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetSearchRealtiesInfiniteQuery,
  useGetSearchRealtiesMapQuery,
  useGetSearchInputRealtiesQuery,
  useGetSearchFiltersDataQuery,
  useGetRealtiesByIdsQuery,
} = searchApi;
